/**
 * Dashboard — valódi adatokkal, név szerinti köszöntés, pénzügyi KPI-k
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { get } from '../../lib/api';
import { Card, Button, Badge } from '../../components/ui';

interface FinanceData {
  monthly: { invoiceCount: number; revenue: number; netTotal: number; vatTotal: number; grossTotal: number };
  open: { count: number; total: number };
  overdue: { count: number; total: number };
  cashBalance: number;
  partnerCount: number;
  dailyRevenue: { date: string; amount: number }[];
  recentInvoices: { invoiceNumber: string; type: string; status: string; gross: number; issueDate: string; partnerName: string }[];
  recentCashReceipts: { receiptNumber: string; receiptType: string; amount: number; status: string; issuedDate: string; reason: string; partnerName: string }[];
}

function fmt(v: number) { return Math.round(v).toLocaleString('hu-HU') + ' Ft'; }
function fmtDate(iso: string) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }); } catch { return '—'; } }
function fmtDateFull(iso: string) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return '—'; } }

function pick<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }

function getTimeGreeting(firstName: string): { greeting: string; mood: string } {
  const h = new Date().getHours();

  if (h < 5) {
    return {
      greeting: pick([
        `Még mindig dolgozol, ${firstName}? 🌙`,
        `Éjjeli műszak, ${firstName}? 😄`,
        `Te még mindig itt vagy, ${firstName}? Na, akkor nézzük. 🌙`,
      ]),
      mood: pick([
        '🌙 Éjjeli munka? Mi nem ítélkezünk.',
        '😄 Még egy gyors feladat, aztán tényleg vége.',
        '☕ Kávé nélkül nincs Modulum.',
      ]),
    };
  }

  if (h < 10) {
    return {
      greeting: pick([
        `Jó reggelt, ${firstName}! ☕`,
        `Jó reggelt, ${firstName}! Indulhat a nap. ☕`,
        `Hajrá, ${firstName}! Szép reggelt! ☕`,
      ]),
      mood: pick([
        '☕ Reggeli kávé, aztán jöhetnek a megrendelések.',
        '☕ A kávé megvan. A megrendelések is megvannak?',
        '🚀 Na, nézzük, mit hoz a mai nap.',
        '📋 Nézzük, mi vár rád ma.',
        '📦 Az ügyfelek már várnak rád.',
      ]),
    };
  }

  if (h < 14) {
    return {
      greeting: pick([
        `Jó napot, ${firstName}! 👋`,
        `Szép napot, ${firstName}!`,
        `Szép napot, ${firstName}! Nézzük, mi vár rád ma.`,
      ]),
      mood: pick([
        '📦 Az ügyfelek már várnak rád.',
        '💡 Egy jó nap egy jól kezelt megrendeléssel kezdődik.',
        '🔥 Ma is lehet haladni egy nagyot.',
        '🤝 Az ügyfeleid számítanak rád.',
        '🚀 Na, nézzük, mit hoz a mai nap.',
      ]),
    };
  }

  if (h < 18) {
    return {
      greeting: pick([
        `Szép délutánt, ${firstName}! 👋`,
        `Na, nézzük, mi maradt mára, ${firstName}!`,
        `Szép délutánt, ${firstName}! Még egy lendület.`,
      ]),
      mood: pick([
        '📋 Nézzük, mi vár még rád.',
        '🔥 Ma is lehet haladni egy nagyot.',
        '🤝 Az ügyfeleid számítanak rád.',
        '💡 Még egy feladat a nap végéig.',
      ]),
    };
  }

  return {
    greeting: pick([
      `Szép estét, ${firstName}! 🌙`,
      `Lassan vége a napnak, ${firstName}.`,
      `Szép estét, ${firstName}! Még egy kör? 🌙`,
    ]),
    mood: pick([
      '🌙 Lassan vége a napnak. Nézzük, mi maradt még.',
      '😄 Még egy gyors feladat, aztán tényleg vége.',
      '📋 Még egy kis összegzés a napról.',
      '🤝 Az ügyfeleid számítanak rád — holnap is.',
    ]),
  };
}

const TYPE_ICONS: Record<string, string> = { normal: '📄', proforma: '📋', cancellation: '↩️', corrective: '✏️' };
const TYPE_LABELS: Record<string, string> = { normal: 'Számla', proforma: 'Díjbekérő', cancellation: 'Sztornó', corrective: 'Helyesbítő' };

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [finance, setFinance] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const firstName = user?.name?.split(' ').pop() || user?.name || '';
  const [timeGreeting] = useState(() => getTimeGreeting(firstName));

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const f = await get<FinanceData>('/dashboard/finance');
      setFinance(f);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { const t = setInterval(loadAll, 5 * 60 * 1000); return () => clearInterval(t); }, [loadAll]);

  const today = new Date();
  const dayName = today.toLocaleDateString('hu-HU', { weekday: 'long' });
  const dateStr = fmtDateFull(today.toISOString());

  const isNew = finance !== null && finance.monthly.invoiceCount === 0 && finance.open.count === 0 && finance.partnerCount === 0;

  if (loading || !finance) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>;
  }

  // ── Üres (új felhasználó) állapot ──
  if (isNew) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👋</div>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy)', marginBottom: '8px' }}>
          {timeGreeting.greeting}
        </h1>
        <p style={{ color: 'var(--slate)', fontSize: '15px', marginBottom: '24px' }}>
          Üdvözlünk a Modulumban! Kezdj a partnereid felvételével, aztán hozz létre egy számlát.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          <Button onClick={() => navigate('/partnerek')}>+ Új partner</Button>
          <Button variant="ghost" onClick={() => navigate('/cikkek')}>+ Új cikk</Button>
          <Button variant="ghost" onClick={() => navigate('/szamlak')}>+ Új számla</Button>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '30px' }}>
          Tipp: A felső menüben minden modult megtalálsz.
        </p>
      </div>
    );
  }

  const maxRev = Math.max(...finance.dailyRevenue.map(r => r.amount), 1);

  return (
    <div>
      {/* Fejléc + köszöntés */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--navy)', margin: 0, lineHeight: 1.3 }}>
            {timeGreeting.greeting}
          </h1>
          <p style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '3px' }}>
            {timeGreeting.mood}
          </p>
          <p style={{ fontSize: '11px', color: 'var(--slate-2)', marginTop: '2px' }}>
            {dateStr}, {dayName}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <Button size="sm" variant="ghost" onClick={loadAll}>Frissítés</Button>
          <Button size="sm" onClick={() => navigate('/szamlak')}>+ Számla</Button>
        </div>
      </div>

      {/* KPI kártyák */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <KpiCard label="Havi bevétel" value={fmt(finance.monthly.revenue)} sub={`${finance.monthly.invoiceCount} számla`} color="#2563eb" onClick={() => navigate('/szamlak')} />
        <KpiCard label="Nyitott számlák" value={String(finance.open.count)} sub={fmt(finance.open.total)} color="#f59e0b" onClick={() => navigate('/szamlak')} />
        <KpiCard label="Lejárt" value={String(finance.overdue.count)} sub={finance.overdue.total > 0 ? fmt(finance.overdue.total) : 'nincs lejárt'} color={finance.overdue.count > 0 ? '#dc2626' : '#16a34a'} onClick={() => navigate('/kintlevoseg')} />
        <KpiCard label="Pénztár egyenleg" value={fmt(finance.cashBalance)} sub="Házipénztár" color="#059669" onClick={() => navigate('/hazipenztar')} />
      </div>

      {/* Bevétel grafikon + havi összesítő */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>

        {/* Forgalom chart */}
        <Card style={{ padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', marginBottom: '12px' }}>Havi forgalom</div>
          {finance.dailyRevenue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)', fontSize: '13px' }}>Nincs adat ebben a hónapban.</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px' }}>
                {finance.dailyRevenue.map((r, i) => (
                  <div key={i} title={`${fmtDate(r.date)}: ${fmt(r.amount)}`}
                    style={{
                      flex: 1, minWidth: '3px', borderRadius: '2px 2px 0 0', cursor: 'pointer',
                      background: 'var(--orange)', opacity: 0.8,
                      height: `${Math.max(2, (r.amount / maxRev) * 100)}%`,
                      transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--slate)', marginTop: '4px' }}>
                <span>{fmtDate(finance.dailyRevenue[0]?.date)}</span>
                <span>{fmtDate(finance.dailyRevenue[finance.dailyRevenue.length - 1]?.date)}</span>
              </div>
            </>
          )}
        </Card>

        {/* Havi összesítő */}
        <Card style={{ padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', marginBottom: '12px' }}>Havi összesítő</div>
          <div style={{ display: 'grid', gap: '8px' }}>
            <SummaryRow label="Nettó forgalom" value={fmt(finance.monthly.netTotal)} />
            <SummaryRow label="ÁFA" value={fmt(finance.monthly.vatTotal)} />
            <SummaryRow label="Bruttó forgalom" value={fmt(finance.monthly.grossTotal)} bold />
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: '8px', marginTop: '4px' }}>
              <SummaryRow label="Kiállított számlák" value={`${finance.monthly.invoiceCount} db`} />
              <SummaryRow label="Partnerek" value={`${finance.partnerCount} db`} />
            </div>
          </div>
        </Card>
      </div>

      {/* Utolsó számlák + bizonylatok */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>

        {/* Utolsó számlák */}
        <Card style={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>Legutóbbi számlák</div>
            <button onClick={() => navigate('/szamlak')} style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Mind →</button>
          </div>
          {finance.recentInvoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--slate)', fontSize: '13px' }}>Nincs számla.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {finance.recentInvoices.map((inv, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: 'var(--r)', background: 'var(--mist)', cursor: 'pointer', fontSize: '12px' }} onClick={() => navigate('/szamlak')}>
                  <span>{TYPE_ICONS[inv.type] || '📄'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)', textTransform: 'uppercase', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</div>
                    <div style={{ fontSize: '10px', color: 'var(--slate)' }}>{inv.partnerName} · {fmtDate(inv.issueDate)}</div>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--navy)', whiteSpace: 'nowrap' }}>{fmt(inv.gross)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Utolsó bizonylatok */}
        <Card style={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>Legutóbbi pénztármozgások</div>
            <button onClick={() => navigate('/hazipenztar')} style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Mind →</button>
          </div>
          {finance.recentCashReceipts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--slate)', fontSize: '13px' }}>Nincs pénztármozgás.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {finance.recentCashReceipts.map((cr, i) => {
                const isBPB = cr.receiptType === 'BPB';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: 'var(--r)', background: 'var(--mist)', cursor: 'pointer', fontSize: '12px' }} onClick={() => navigate('/hazipenztar')}>
                    <span>{isBPB ? '📥' : '📤'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)', textTransform: 'uppercase', fontSize: '11px' }}>{cr.receiptNumber}</div>
                      <div style={{ fontSize: '10px', color: 'var(--slate)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cr.reason}{cr.partnerName ? ` · ${cr.partnerName}` : ''}</div>
                    </div>
                    <span style={{ fontWeight: 700, color: isBPB ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>{isBPB ? '+' : '−'}{fmt(cr.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Gyors akciók */}
      <div style={{ marginTop: '20px', padding: '16px', background: 'var(--mist)', borderRadius: 'var(--r)', display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
        <Button size="sm" onClick={() => navigate('/szamlak')}>+ Számla</Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/megrendelesek')}>+ Megrendelés</Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/hazipenztar')}>Házipénztár</Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/partnerek')}>+ Partner</Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/beolvaso')}>📷 Beolvasó</Button>
      </div>
    </div>
  );
}

// ── KPI kártya ──
function KpiCard({ label, value, sub, color, onClick }: { label: string; value: string; sub: string; color: string; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={{
      padding: '14px 16px', borderRadius: 'var(--r)',
      background: 'var(--white)', border: '1px solid var(--line)',
      borderLeft: `3px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
    }}>
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>{sub}</div>
    </div>
  );
}

// ── Összesítő sor ──
function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: bold ? '14px' : '13px', fontWeight: bold ? 700 : 400, color: bold ? 'var(--navy)' : 'var(--ink)', padding: '2px 0' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
